import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';
import { OrderService } from '../../core/http/order.service';
import { trigger, state, style, animate, transition } from '@angular/animations';
import {TableNumberService} from "../../core/http/table-number.service";
import {CategoryDto} from "../../shared/models/category-dto";
import {ItemsService} from "../../core/http/items.service";
import { v4 as uuidv4 } from 'uuid';
import {Item} from "../../shared/models/item";
import {OrderPosition} from "../../shared/models/OrderPosition";
import {AuthService} from "../../core/auth/auth.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {filter, finalize, take} from "rxjs";
import {cloneDeep} from "lodash-es";

@Component({
  selector: 'app-items-grid',
  templateUrl: './items-overview.component.html',
  styleUrls: ['./items-overview.component.scss'],
  animations: [
    trigger('slideInOut', [
      state(
        'void',
        style({
          transform: 'translateY(100%)',
          opacity: 0
        })
      ),
      state(
        '*',
        style({
          transform: 'translateY(0%)',
          opacity: 1
        })
      ),
      transition(':enter', animate('300ms ease-in-out')),
      transition(':leave', animate('300ms ease-in-out'))
    ])
  ]
})
export class ItemsOverviewComponent implements OnInit, OnDestroy {

  itemOverviewData: CategoryDto[] = [];
  itemsCounter: number = 0;
  totalAmount: number = 0;
  localCommentsMap = new Map<string, string>();
  isLoading = false;
  commentTexts: { [key: string]: string } = {};

  constructor(private router: Router,
              public orderService: OrderService,
              public tableNumberService: TableNumberService,
              private itemsService: ItemsService,
              private authService: AuthService,
              private _snackBar: MatSnackBar) {

  }

  ngOnInit() {
    //this.orderService.clearOrder();
    this.itemsService.getAllAvailableCategoriesAndItems().subscribe(data => {
      this.itemOverviewData = data;
    });
    sessionStorage.setItem('tableNumberState', JSON.stringify({
      tableNumber: this.tableNumberService.getTableNumber()
    }));
    const orderFromState = sessionStorage.getItem('orderState');
    if (orderFromState) {
      const itemsCounterFromState = JSON.parse(orderFromState).itemsCounter;
      const totalAmountFromState = JSON.parse(orderFromState).totalAmount;
      if (itemsCounterFromState && totalAmountFromState) {
        this.itemsCounter = itemsCounterFromState;
        this.totalAmount = totalAmountFromState;
      }
    }
    const commentsFromState = sessionStorage.getItem('commentsState');
    console.log(commentsFromState);
    if (commentsFromState) {
      const parsed = JSON.parse(commentsFromState);
      this.localCommentsMap = new Map<string, string>(parsed.comments);
      this.localCommentsMap.forEach((value, key) => {
        this.commentTexts[key] = value;
      });
    }
  }

  ngOnDestroy() {
    sessionStorage.removeItem('orderState');
    sessionStorage.removeItem('tableNumberState');
    sessionStorage.removeItem('commentsState');
  }

  addItem(item: Item): void {
    if (!this.orderService.getOrder()) {
      const commentCategories = this.itemOverviewData.filter(cat => cat.category.allowComment).map(cat => cat.category.text);
      this.orderService.setOrder({
        guid: uuidv4(),
        tableNumber: this.tableNumberService.getTableNumber(),
        waiterGuid: this.authService.getUserGuid() ? this.authService.getUserGuid() : '',
        waiterName: this.authService.getUserName() ? this.authService.getUserName() : '',
        comments: '',
        printReceipt: false,
        reprint: false,
        createdBy: '',
        created: '',
        positions: []
      });
    }
    // search item
    if (this.orderService.getOrder().positions!.find(pos => pos.itemGuid === item.guid)) {
      // if item found, add 1 to quantity
      this.orderService.getOrder().positions!.find(pos => pos.itemGuid === item.guid)!.quantity++;
      this.itemsCounter++;
      this.totalAmount += item.price;
    } else {
      const position: OrderPosition = {
        guid: uuidv4(),
        categoryIdentifier: item.categoryIdentifier,
        printCategoryIdentifier: item.printCategoryIdentifier,
        itemGuid: item.guid,
        name: item.name,
        itemPrice: item.price,
        quantity: 1,
        cancelledQuantityRequested: 0,
        cancelledQuantityAccepted: 0,
        sortId: item.sortId
      };
      this.orderService.getOrder().positions!.push(position);
      this.itemsCounter++;
      this.totalAmount += item.price;
    }
    sessionStorage.setItem('orderState', JSON.stringify({
      order: cloneDeep(this.orderService.getOrder()),
      itemsCounter: this.itemsCounter,
      totalAmount: this.totalAmount
    }));
  }

  removeItem(item: Item): void {
    if (this.orderService.getOrder() && this.orderService.getOrder().positions) {
      const position = this.orderService.getOrder().positions!.find(pos => pos.itemGuid === item.guid);
      if (position) {
        if (position.quantity == 1) {
          // delete position
          const positionIndex = this.orderService.getOrder().positions!.findIndex(pos => pos.itemGuid === item.guid);
          if (positionIndex > -1) {
            this.orderService.getOrder().positions!.splice(positionIndex, 1);
            this.itemsCounter--;
            this.totalAmount -= item.price;
          }
        } else {
          // decrement quantity by 1
          position.quantity--;
          this.itemsCounter--;
          this.totalAmount -= item.price;
        }
      }
    }
    sessionStorage.setItem('orderState', JSON.stringify({
      order: cloneDeep(this.orderService.getOrder()),
      itemsCounter: this.itemsCounter,
      totalAmount: this.totalAmount
    }));
  }

  addComment(comment: string, categoryIdentifier: string) {
    if (!this.localCommentsMap.has(categoryIdentifier)) {
      this.localCommentsMap.set(categoryIdentifier, comment);
    } else {
      // If the key already exists, update its value
      this.localCommentsMap.set(categoryIdentifier, comment);
    }
    sessionStorage.setItem('commentsState', JSON.stringify({
      comments: Array.from(this.localCommentsMap.entries())
    }));
  }

  getItemQuantity(item: Item): number {
    if (this.orderService.getOrder() && this.orderService.getOrder().positions && this.orderService.getOrder()!.positions!.length > 0) {
      const position = this.orderService.getOrder()!.positions!.find(pos => pos.itemGuid == item.guid);
      if (position) {
        return position.quantity;
      }
    }
    return 0;
  }

  getButtonFontSize(item: string): string {
    const maxLength = 20; // Maximum length before reducing font size
    const defaultFontSize = 16; // Default font size
    const reductionFactor = 0.5; // Font size reduction factor

    if (item.length > maxLength) {
      const fontSize = defaultFontSize - Math.floor((item.length - maxLength) * reductionFactor);
      return `${fontSize}px`;
    }
    return `${defaultFontSize}px`;
  }

  openSummary() {
    if (this.itemsCounter > 0) {
      this.orderService.getOrder().comments = JSON.stringify(Array.from(this.localCommentsMap));
      this.isLoading = true;
      this.orderService.sendTableOrder(this.orderService.getOrder()).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe({
        next: (response) => {
          console.log('send table order');
          // we can directly navigate here as next is only called when status is 200
          this.router.navigate(['/checkout']).then(() => {});
        },
        error: (err) => {
          if (err.status === 304) {
            console.warn('Bestellung wurde bereits gesendet (304). Navigiere trotzdem weiter.');
            this.router.navigate(['/checkout']).then(() => {});
          } else {
            console.error('Fehler beim Senden der Bestellung', err);
            this._snackBar.open('Fehler beim Senden der Bestellung. Bitte erneut versuchen.', 'Schließen', { duration: 10000 });
          }
        }
      });
   }

  }

}
